import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { BsSearch, BsX } from 'react-icons/bs';

import Panel from '#globalComponents/Overlay/Panel';
import { LAYER } from '#lib/constants/layers';

const MIN_CHARS = 2;

/**
 * Wrap query-term occurrences in <mark> for result snippets.
 */
export function highlightMatch(text, term) {
	if (!text) return null;
	const trimmed = term?.trim();
	if (!trimmed) return text;
	const escaped = trimmed.replaceAll(/[.*+?^${}()|[\]\\]/g, '\\$&');
	const parts = String(text).split(new RegExp(`(${escaped})`, 'gi'));
	return parts.map((part, i) =>
		part.toLowerCase() === trimmed.toLowerCase() ? (
			<mark key={i} className="bg-yellow-100 text-inherit rounded-sm">
				{part}
			</mark>
		) : (
			part
		),
	);
}

const SearchOverlay = ({
	open,
	onOpenChange,
	placeholder,
	query,
	onQueryChange,
	groups,
	renderItem,
	getItemKey,
	onSelect,
	isLoading,
	pendingItemKey,
	errorMessage,
}) => {
	const [activeIdx, setActiveIdx] = useState(-1);
	const inputRef = useRef(null);

	const flatItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);
	const isSearchable = query.trim().length >= MIN_CHARS;
	const hasPending = Boolean(pendingItemKey);

	const close = useCallback(() => {
		onQueryChange('');
		setActiveIdx(-1);
		onOpenChange(false);
	}, [onOpenChange, onQueryChange]);

	useEffect(() => {
		if (open) setTimeout(() => inputRef.current?.focus(), 50);
	}, [open]);

	useEffect(() => {
		if (!open) return;
		const handler = (e) => {
			if (e.key === 'Escape') close();
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, [open, close]);

	useEffect(() => {
		setActiveIdx(-1);
	}, [query]);

	const handleKeyDown = (e) => {
		if (flatItems.length === 0 || hasPending) return;
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setActiveIdx((i) => Math.min(i + 1, flatItems.length - 1));
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setActiveIdx((i) => Math.max(i - 1, 0));
		} else if (e.key === 'Enter' && activeIdx >= 0) {
			e.preventDefault();
			onSelect(flatItems[activeIdx]);
		}
	};

	return (
		<>
			<button
				type="button"
				onClick={() => onOpenChange(true)}
				title={placeholder}
				aria-label={placeholder}
				className="p-2 text-bgBase-800 hover:text-primary-600 hover:bg-purple-50 rounded-lg transition-colors"
			>
				<BsSearch className="w-5 h-5" />
			</button>

			{open && (
				<div className={`fixed inset-0 ${LAYER.modal} flex`}>
					<div className="absolute inset-0 bg-text/40" onClick={close} />

					<Panel className="relative mx-auto mt-16 mb-16 w-full max-w-2xl max-h-[calc(100vh-8rem)] h-fit">
						<div className="flex items-center gap-3 px-5 py-3.5 border-b border-bgBase-400">
							<BsSearch className="w-4 h-4 text-bgBase-800 flex-shrink-0" />
							<input
								ref={inputRef}
								type="text"
								value={query}
								onChange={(e) => onQueryChange(e.target.value)}
								onKeyDown={handleKeyDown}
								placeholder={placeholder}
								className="flex-1 border-0 bg-transparent p-0 rounded-none text-sm outline-none focus:ring-0 placeholder:text-bgBase-800"
								autoComplete="off"
							/>
							{isLoading && (
								<div className="w-4 h-4 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin flex-shrink-0" />
							)}
							{query && !isLoading && (
								<button
									type="button"
									onClick={() => {
										onQueryChange('');
										inputRef.current?.focus();
									}}
									className="p-0.5 text-bgBase-800 hover:text-text"
								>
									<BsX className="w-4 h-4" />
								</button>
							)}
							<button
								type="button"
								onClick={close}
								className="ml-1 text-xs text-bgBase-800 bg-gray-100 rounded px-1.5 py-0.5 hover:bg-gray-200"
							>
								ESC
							</button>
						</div>

						<div className="flex-1 min-h-0 overflow-y-auto">
							{errorMessage && (
								<div className="px-4 py-2.5 text-sm text-warning-700 bg-red-50 border-b border-red-100">
									{errorMessage}
								</div>
							)}

							{!isSearchable && (
								<div className="px-6 py-12 text-center text-sm text-bgBase-800">
									Mindestens 2 Zeichen eingeben …
								</div>
							)}

							{isSearchable && isLoading && flatItems.length === 0 && (
								<div className="flex justify-center py-12">
									<div className="w-6 h-6 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
								</div>
							)}

							{isSearchable && !isLoading && flatItems.length === 0 && (
								<div className="px-6 py-12 text-center text-sm text-bgBase-800">
									Keine Ergebnisse für &bdquo;{query.trim()}&ldquo;
								</div>
							)}

							{groups.map((group) => {
								if (group.items.length === 0) return null;
								const groupOffset = flatItems.indexOf(group.items[0]);
								return (
									<div key={group.key}>
										<div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-bgBase-800">
											{group.label}
										</div>
										{group.items.map((item, idx) => {
											const itemKey = getItemKey(item);
											const flatIdx = groupOffset + idx;
											const isPendingItem = pendingItemKey === itemKey;
											return (
												<button
													key={itemKey}
													type="button"
													disabled={hasPending}
													onClick={() => onSelect(item)}
													className={`w-full text-left px-4 py-2.5 flex items-start gap-3 transition-colors disabled:opacity-50 ${
														activeIdx === flatIdx ? 'bg-primary-100' : 'hover:bg-gray-50'
													} ${isPendingItem ? '!opacity-100' : ''}`}
												>
													{renderItem(item, { isPending: isPendingItem })}
												</button>
											);
										})}
									</div>
								);
							})}
						</div>
					</Panel>
				</div>
			)}
		</>
	);
};

SearchOverlay.propTypes = {
	open: PropTypes.bool.isRequired,
	onOpenChange: PropTypes.func.isRequired,
	placeholder: PropTypes.string.isRequired,
	query: PropTypes.string.isRequired,
	onQueryChange: PropTypes.func.isRequired,
	groups: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			items: PropTypes.array.isRequired,
		}),
	).isRequired,
	renderItem: PropTypes.func.isRequired,
	getItemKey: PropTypes.func.isRequired,
	onSelect: PropTypes.func.isRequired,
	isLoading: PropTypes.bool,
	pendingItemKey: PropTypes.string,
	errorMessage: PropTypes.string,
};

export default SearchOverlay;
